import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";

import { APIEndpoints } from "../constants/API";
import { Timeouts } from "../constants/Common";
import { AUTH } from "../constants/routes";
import { buildLoginPath } from "../utils/authNavigation";
import { getErrorMessage } from "../utils/sharedErrorHandler";

export interface IApiError {
  error?: string;
  status: number;
  originalError?: unknown;
  /** Seconds to wait before retrying, parsed from Retry-After header on 429 responses */
  retryAfter?: number;
}

export class ApiError extends Error implements IApiError {
  status: number;
  originalError?: unknown;
  /** Seconds to wait before retrying, parsed from Retry-After header on 429 responses */
  retryAfter?: number;

  constructor(
    error: string,
    status: number,
    originalError?: unknown,
    retryAfter?: number,
  ) {
    super(error);
    this.status = status;
    this.originalError = originalError;
    this.retryAfter = retryAfter;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

interface IApiCaller {
  get<T>(): Promise<T>;
  post<T, D = unknown>(data: D): Promise<T>;
  put<T, D = unknown>(data: D): Promise<T>;
  delete<T>(): Promise<T>;
  patch<T, D = unknown>(data: D): Promise<T>;
}

class API implements IApiCaller {
  private api: string;
  private endpoint: string;
  private config: AxiosRequestConfig;
  private retryCount: number = 0;
  private maxRetries: number = 3;

  constructor(api: string, endpoint: string) {
    // Sanitize base URL to avoid double /api
    if (api.endsWith("/api")) {
      api = api.slice(0, -4);
    } else if (api.endsWith("/api/")) {
      api = api.slice(0, -5);
    }
    this.api = api;
    this.endpoint = endpoint;
    this.config = this.createConfig();

    // Add request interceptor for 401 handling
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const requestUrl = error.config?.url ?? "";
        const isAuthBootstrapRequest = requestUrl.endsWith(
          APIEndpoints.AUTH_ME,
        );
        const isRefreshRequest = requestUrl.endsWith("/api/v1/auth/refresh");
        const request = error.config as
          (AxiosRequestConfig & { authRefreshRetry?: boolean }) | undefined;

        if (
          error.response?.status === 401 &&
          this.retryCount < this.maxRetries &&
          !isAuthBootstrapRequest &&
          !isRefreshRequest &&
          !request?.authRefreshRetry
        ) {
          this.retryCount++;
          try {
            await axios.post(
              `${this.api}/api/v1/auth/refresh`,
              undefined,
              this.createConfig(),
            );
            this.retryCount = 0;
            if (request) {
              request.authRefreshRetry = true;
              return axios(request);
            }
          } catch {
            // Session refresh failed — redirect to login.
            this.handleAuthError();
          }
        }
        return Promise.reject(error);
      },
    );
  }

  private createConfig(): AxiosRequestConfig {
    return {
      withCredentials: true, // Send httpOnly session cookie
    };
  }

  public updateConfigHeaders(headers: Record<string, string>): void {
    this.config = {
      ...this.config,
      headers: {
        ...this.config.headers,
        ...headers,
      },
    };
  }

  private handleAuthError(): void {
    // Auth bootstrap and public auth pages intentionally receive 401 when no
    // session cookie exists. Redirecting from there reloads the same page and
    // creates an authentication loop.
    if (window.location.pathname.startsWith(AUTH.ROOT.path)) {
      return;
    }

    orgSessionManager.clearSession();
    window.location.href = buildLoginPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  }

  private async request<T>(
    method: "get" | "post" | "put" | "delete" | "patch",
    data?: unknown,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await axios({
        method,
        url: `${this.api}${this.endpoint}`,
        data,
        ...this.config,
        timeout: Timeouts.API_REQUEST,
      });
      this.retryCount = 0; // Reset retry count on successful request
      return response.data;
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      const status = axios.isAxiosError(err)
        ? err.response?.status || 500
        : 500;

      // Extract Retry-After header for 429 responses
      let retryAfter: number | undefined;
      if (status === 429 && axios.isAxiosError(err) && err.response) {
        const retryAfterHeader = err.response.headers?.["retry-after"];
        if (typeof retryAfterHeader === "string") {
          const parsed = parseInt(retryAfterHeader, 10);
          if (!isNaN(parsed) && parsed > 0) {
            retryAfter = parsed;
          }
        }
      }

      throw new ApiError(errorMessage, status, err, retryAfter);
    }
  }

  public get<T>(): Promise<T> {
    return this.request<T>("get");
  }

  public post<T, D = unknown>(data: D): Promise<T> {
    return this.request<T>("post", data);
  }

  public put<T, D = unknown>(data: D): Promise<T> {
    return this.request<T>("put", data);
  }

  public delete<T>(): Promise<T> {
    return this.request<T>("delete");
  }

  public patch<T, D = unknown>(data: D): Promise<T> {
    return this.request<T>("patch", data);
  }
}

export default API;
